import { Typography, useTheme } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { ContactGroupType } from 'contacts/enums';
import { IContactGroupAddress } from 'contacts/types';
import { LabeledValue } from 'core/elements';
import { getAddressToDisplay } from 'core/utils';
import { Level, Size } from 'theme/enums';

interface IProps {
  contactGroupAddress: Partial<IContactGroupAddress>;
  contactGroupType?: ContactGroupType;
  displayName?: boolean;
  displayARNumber?: boolean;
  displayCustomerNumber?: boolean;
  displayAPNumber?: boolean;
  size?: Size;
}

export const Location = ({
  contactGroupAddress,
  contactGroupType = ContactGroupType.CUSTOMER,
  displayName = false,
  displayARNumber = true,
  displayCustomerNumber = true,
  displayAPNumber = true,
  size = Size.MD,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const labelLevel = (() => {
    switch (size) {
      case Size.SM:
        return Level.BODY_XS;
      case Size.MD:
      default:
        return Level.BODY_SM;
    }
  })();

  const valueLevel = (() => {
    switch (size) {
      case Size.SM:
        return Level.BODY_XS;
      case Size.MD:
      default:
        return Level.TITLE_SM;
    }
  })();

  return (
    <>
      {contactGroupType === ContactGroupType.CUSTOMER && (
        <>
          {displayName && (
            <Typography level={Level.TITLE_MD}>{contactGroupAddress?.name}</Typography>
          )}
          {displayARNumber && (
            <LabeledValue
              label={t('labelARNumber')}
              value={contactGroupAddress?.accountsReceivableId}
              options={{
                labelLevel,
                valueLevel,
                labelSx: { fontWeight: 400 },
                valueSx: {
                  color: theme.palette.neutral[800],
                  fontWeight: 500,
                },
                inline: true,
              }}
            />
          )}

          {displayCustomerNumber && (
            <LabeledValue
              label={t('labelCustomerNumber')}
              value={contactGroupAddress?.externalCustomerNumber}
              options={{
                labelLevel,
                valueLevel,
                labelSx: { fontWeight: 400 },
                valueSx: {
                  color: theme.palette.neutral[800],
                  fontWeight: 500,
                },
                inline: true,
              }}
            />
          )}
        </>
      )}

      {contactGroupType === ContactGroupType.VENDOR && (
        <>
          {displayAPNumber && (
            <LabeledValue
              label={t('labelAPNumber')}
              value={contactGroupAddress?.accountsPayableId}
              options={{
                labelLevel,
                valueLevel,
                labelSx: { fontWeight: 400 },
                valueSx: { fontWeight: 500 },
                inline: true,
              }}
            />
          )}
        </>
      )}

      <Typography level={valueLevel} fontWeight={500} sx={{ color: theme.palette.neutral[800] }}>
        {getAddressToDisplay(contactGroupAddress, {
          displayStateName: true,
          displayCountryName: true,
        }).join(', ')}
      </Typography>
    </>
  );
};
